import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Send, Megaphone, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";
import SocialCalendar from "@/components/SocialCalendar";

const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const SEGMENT_LABELS = { all: "All clients", outstanding: "With unpaid invoices", warm: "Active recently (90 days)", lapsed: "Gone quiet (6+ months)" };
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function AdminMarketing() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("calendar");
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [camp, setCamp] = useState({ segment: "all", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [socials, setSocials] = useState({ facebook: "", instagram: "", tiktok: "", linkedin: "" });
  const [savingSocials, setSavingSocials] = useState(false);

  const shareText = () => `${camp.subject}\n\n${camp.message}`.trim();
  const shareLinks = () => {
    const text = encodeURIComponent(shareText());
    const url = encodeURIComponent(window.location.origin);
    return [
      ["Facebook", `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`, "share-facebook"],
      ["X", `https://twitter.com/intent/tweet?text=${text}`, "share-x"],
      ["WhatsApp", `https://wa.me/?text=${text}`, "share-whatsapp"],
      ["LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "share-linkedin"],
    ];
  };

  const saveSocials = async () => {
    setSavingSocials(true);
    try {
      await api.put("/settings/socials", socials);
      toast.success("Social links saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSocials(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: o }, { data: c }, { data: so }] = await Promise.all([
        api.get("/marketing/overview"), api.get("/marketing/campaigns"), api.get("/settings/socials"),
      ]);
      setOverview(o);
      setCampaigns(c);
      setSocials(so);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendCampaign = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/marketing/campaigns", camp);
      toast.success(data.message);
      setCamp({ segment: "all", subject: "", message: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="marketing-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/admin")} data-testid="marketing-back"
            className="h-11 w-11 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
            <ChevronLeft className="h-5 w-5 text-[#0F2547]" />
          </button>
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]">Marketing</h1>
            <p className="text-xs text-slate-500">Social calendar &amp; client campaigns</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex gap-2 flex-wrap" data-testid="marketing-tabs">
          {[["calendar", "Social calendar", CalendarDays], ["campaigns", "Campaigns", Megaphone]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`marketing-tab-${k}`}
              className={`h-10 px-4 rounded-lg text-xs font-bold inline-flex items-center gap-1.5 transition-colors ${tab === k ? "bg-[#0F2547] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-[#005EB8]"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "calendar" && <SocialCalendar />}

        {tab === "campaigns" && (
          <>
            <form onSubmit={sendCampaign} className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="campaign-form">
              <h2 className="font-heading text-base font-bold text-[#0F2547]">Send a campaign</h2>
              <label className="block">
                <span className="text-xs font-semibold text-slate-600 block mb-1">Who's it going to?</span>
                <select value={camp.segment} onChange={(e) => setCamp((f) => ({ ...f, segment: e.target.value }))}
                  data-testid="campaign-segment" className={`${inputCls} bg-white`}>
                  {Object.entries(SEGMENT_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}{overview ? ` — ${overview.segments[k] || 0} with email` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <input placeholder="Subject — e.g. Book your 2027 medical cover early" value={camp.subject}
                onChange={(e) => setCamp((f) => ({ ...f, subject: e.target.value }))} data-testid="campaign-subject" className={inputCls} />
              <div className="flex items-start gap-2">
                <textarea rows={5} required value={camp.message}
                  onChange={(e) => setCamp((f) => ({ ...f, message: e.target.value }))} data-testid="campaign-message"
                  placeholder="Hi {name}, — use {name} for their first name and {client} for the company. Type or tap the mic and talk…"
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                <div className="pt-1.5">
                  <VoiceButton testid="campaign-voice" onTranscript={(t) => setCamp((f) => ({ ...f, message: (f.message ? f.message + " " : "") + t }))} />
                </div>
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Share this post yourself — opens with the text ready</p>
                <div className="flex gap-2 flex-wrap" data-testid="campaign-share-row">
                  {shareLinks().map(([label, href, testid]) => (
                    <a key={label} href={href} target="_blank" rel="noreferrer" data-testid={`campaign-${testid}`}
                      className={`h-9 px-3 rounded-lg border text-xs font-bold inline-flex items-center transition-colors ${camp.message.trim() ? "border-slate-300 text-[#005EB8] hover:border-[#005EB8]" : "border-slate-200 text-slate-300 pointer-events-none"}`}>
                      {label}
                    </a>
                  ))}
                  <span className="text-[10px] text-slate-400 self-center">TikTok has no text share — copy the text and post in the app.</span>
                </div>
              </div>
              <button type="submit" disabled={sending || !camp.subject.trim() || !camp.message.trim()} data-testid="campaign-send"
                className="w-full h-12 rounded-xl bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-sm font-bold inline-flex items-center justify-center gap-2">
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send campaign"}
              </button>
            </form>

            <section className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="campaign-history">
              <h2 className="font-heading text-base font-bold text-[#0F2547] mb-2">Past campaigns</h2>
              {campaigns.length === 0 ? (
                <p className="text-xs text-slate-400">None sent yet.</p>
              ) : campaigns.map((c) => (
                <div key={c.id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`campaign-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#0F2547] truncate">{c.subject}</p>
                    <p className="text-[11px] text-slate-400">
                      {SEGMENT_LABELS[c.segment] || c.segment} · {fmtD(c.created_at)} · by {c.created_by}
                    </p>
                  </div>
                  <span className="text-[11px] font-bold text-[#007F3B] bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 shrink-0">
                    {c.sent_count} sent{c.failed.length ? ` · ${c.failed.length} failed` : ""}
                  </span>
                </div>
              ))}
            </section>
          </>
        )}

        <section className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="socials-card">
          <h2 className="font-heading text-base font-bold text-[#0F2547]">Your social profiles</h2>
          <p className="text-[11px] text-slate-400">Saved here so the team can jump straight to them. Auto-posting to Facebook/Instagram/TikTok/LinkedIn is being set up separately.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[["facebook", "Facebook page URL"], ["instagram", "Instagram profile URL"], ["tiktok", "TikTok profile URL"], ["linkedin", "LinkedIn page URL"]].map(([k, ph]) => (
              <input key={k} value={socials[k]} onChange={(e) => setSocials((s) => ({ ...s, [k]: e.target.value }))}
                placeholder={ph} data-testid={`socials-${k}`} className={inputCls} />
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={saveSocials} disabled={savingSocials} data-testid="socials-save"
              className="h-10 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-xs font-bold">
              {savingSocials ? "Saving…" : "Save links"}
            </button>
            {Object.entries(socials).filter(([, v]) => v).map(([k, v]) => (
              <a key={k} href={v} target="_blank" rel="noreferrer" data-testid={`socials-open-${k}`}
                className="h-10 px-3 rounded-lg border border-slate-300 text-xs font-bold text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] inline-flex items-center capitalize transition-colors">
                {k}
              </a>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
